import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  ShieldAlert,
  RefreshCw,
  Eye,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  Skeleton,
  Badge,
} from '@ouropix/ui';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useAlerts } from '@/hooks/useAlerts';
import { AlertFiltersBar } from '@/components/alerts/AlertFiltersBar';
import { SeverityBadge } from '@/components/alerts/SeverityBadge';
import { StatusBadge } from '@/components/alerts/StatusBadge';
import { EventCodeBadge } from '@/components/alerts/EventCodeBadge';
import { AcknowledgeButton } from '@/components/alerts/AcknowledgeButton';
import { ResolveButton } from '@/components/alerts/ResolveDialog';
import type { AlertFilters, SecurityAlert } from '@/components/alerts/types';

export const Route = createFileRoute('/_admin/alertas-seguranca/')({
  component: AlertasSegurancaPage,
});

function fmt(d?: string | null) {
  if (!d) return '—';
  try {
    return format(new Date(d), 'dd/MM/yy HH:mm', { locale: ptBR });
  } catch {
    return d;
  }
}

function KpiCard({
  label,
  value,
  className,
}: {
  label: string;
  value: number | string;
  className?: string;
}) {
  return (
    <Card className="bg-card/50">
      <CardContent className="p-3">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className={`text-xl font-bold ${className ?? ''}`}>{value}</div>
      </CardContent>
    </Card>
  );
}

function AlertasSegurancaPage() {
  const [filters, setFilters] = useState<AlertFilters>({ page: 1, limit: 25 });
  const { data, isLoading, error, refetch, isFetching } = useAlerts(filters);

  const alerts: SecurityAlert[] = data?.data ?? [];
  const kpis = data?.kpis;
  const totalPages = data?.total_pages ?? 1;
  const currentPage = filters.page ?? 1;

  return (
    <div className="space-y-4 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <ShieldAlert className="h-7 w-7" aria-hidden="true" />
            Alertas de Seguranca
          </h1>
          <p className="text-muted-foreground text-sm">
            Monitoramento em tempo real de eventos suspeitos
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
          aria-label="Atualizar lista de alertas"
        >
          <RefreshCw
            className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`}
            aria-hidden="true"
          />
          Atualizar
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard label="Abertos" value={kpis?.total_open ?? 0} className="text-red-400" />
        <KpiCard
          label="Criticos abertos"
          value={kpis?.total_critical_open ?? 0}
          className="text-red-500"
        />
        <KpiCard label="Ultimas 24h" value={kpis?.total_24h ?? 0} />
        <KpiCard
          label="Evento mais frequente"
          value={kpis?.top_event_code ?? '—'}
          className="text-sm"
        />
      </div>

      {/* Filtros */}
      <AlertFiltersBar filters={filters} onChange={setFilters} />

      {/* Tabela */}
      <Card className="bg-card/50">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center py-12 gap-3">
              <ShieldAlert className="h-12 w-12 text-muted-foreground" aria-hidden="true" />
              <p className="text-muted-foreground">Erro ao carregar alertas.</p>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                <RefreshCw className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
                Tentar novamente
              </Button>
            </div>
          ) : alerts.length === 0 ? (
            <div className="flex flex-col items-center py-12 gap-2">
              <ShieldAlert className="h-12 w-12 text-green-500" aria-hidden="true" />
              <p className="text-muted-foreground">Nenhum alerta encontrado com esses filtros.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table
                className="w-full text-sm"
                role="table"
                aria-label="Lista de alertas de seguranca"
              >
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">
                      Severidade
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">
                      Evento
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground hidden sm:table-cell">
                      Servico
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground hidden md:table-cell">
                      IP
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground hidden lg:table-cell">
                      Alvo
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">
                      Count
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground hidden sm:table-cell">
                      Primeiro
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">
                      Status
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">
                      Acoes
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.map((alert) => (
                    <tr
                      key={alert.id}
                      className="border-b last:border-0 hover:bg-muted/20 transition-colors"
                      role="row"
                    >
                      <td className="px-4 py-3">
                        <SeverityBadge severity={alert.severity} />
                      </td>
                      <td className="px-4 py-3">
                        <EventCodeBadge eventCode={alert.event_code} showIcon={false} />
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        {alert.source_service ? (
                          <code className="text-xs text-muted-foreground">
                            {alert.source_service}
                          </code>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <code className="text-xs text-muted-foreground">
                          {alert.actor_ip ?? '—'}
                        </code>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className="text-xs font-mono text-muted-foreground truncate max-w-[100px] block">
                          {alert.target_id ?? '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="text-xs">
                          {alert.count}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <span className="text-xs text-muted-foreground font-mono">
                          {fmt(alert.first_seen)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={alert.status} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Button variant="ghost" size="sm" className="h-7 px-2" asChild>
                            <Link
                              to="/alertas-seguranca/$id"
                              params={{ id: alert.id }}
                              aria-label={`Ver detalhes do alerta ${alert.id}`}
                            >
                              <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                            </Link>
                          </Button>
                          {alert.status === 'open' && (
                            <AcknowledgeButton
                              alertId={alert.id}
                              size="sm"
                              variant="ghost"
                            />
                          )}
                          {alert.status !== 'resolved' && (
                            <ResolveButton alertId={alert.id} size="sm" variant="ghost" />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div
              className="flex items-center justify-between p-4 border-t"
              role="navigation"
              aria-label="Paginacao de alertas"
            >
              <span className="text-xs text-muted-foreground">
                Pagina {currentPage} de {totalPages} — {data?.total ?? 0} alertas
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage <= 1}
                  onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) - 1 }))}
                  aria-label="Pagina anterior"
                >
                  <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= totalPages}
                  onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) + 1 }))}
                  aria-label="Proxima pagina"
                >
                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
